import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AxiosError } from 'axios';
import { PopUpNotification } from 'src/interface';
import { uuidV4 } from 'src/helper';
import { RootState } from 'src/store';
import {
  addNotification,
  removeNotification,
  clearTimeout,
} from 'src/store/reducers/popUpNotifications';

type UsePopUpNotifications = {
  notifications: PopUpNotification[];
  addSuccessNotification: (title: string, timeout?: number) => void;
  addErrorNotification: (error?: unknown, timeout?: number) => void;
  removeNotification: (uuid: string) => void;
  clearTimeout: (uuid: string) => void;
};

export const usePopUpNotifications = (): UsePopUpNotifications => {
  const dispatch = useDispatch();

  return {
    notifications: useSelector(
      (state: RootState): PopUpNotification[] =>
        state.popUpNotifications.notifications,
    ),
    addSuccessNotification: useCallback(
      (title: string, timeout = 3000) => {
        const uuid = uuidV4();

        dispatch(
          addNotification({
            uuid,
            type: 'success',
            title,
            timeout,
          }),
        );
      },
      [dispatch],
    ),
    addErrorNotification: useCallback(
      (error?: unknown, timeout: number = 3000) => {
        const uuid = uuidV4();

        let title = 'error.unknownError';

        if (typeof error === 'string') {
          title = error;
        } else {
          const axiosResponse = (error as AxiosError<{ detail: string }>)
            ?.response;

          if (axiosResponse) {
            title = `${axiosResponse.data.detail} [ ${axiosResponse.status} ]`;
          }
        }

        dispatch(
          addNotification({
            uuid,
            type: 'error',
            title,
            timeout,
          }),
        );
      },
      [dispatch],
    ),
    removeNotification: useCallback(
      (uuid: string) => {
        dispatch(removeNotification(uuid));
      },
      [dispatch],
    ),
    clearTimeout: useCallback(
      (uuid: string) => {
        dispatch(clearTimeout(uuid));
      },
      [dispatch],
    ),
  };
};
